import { io, Socket } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';
export const RECONNECT_AFTER_SERVER_DROP_MS = 1000;
/** R4-A3: the server refused the login on (re)connect — the session is over, as for a 401 on REST. */
export const SOCKET_SESSION_CODES = ['TOKEN_EXPIRED', 'TOKEN_INVALID', 'TOKEN_REVOKED', 'TOKEN_REQUIRED'];
class SocketService {
    private socket: Socket | null = null;

    connect() {
        // R2 D8: one socket per tab — a second connect() while the first is still handshaking reuses it.
        if (this.socket) return;

        // R4-A3: the token is read on every (re)connect, so a re-login after a deploy restart is picked up.
        this.socket = io(SOCKET_URL, {
            auth: (cb: (data: object) => void) => cb({ token: localStorage.getItem('jwtToken'), type: 'admin' }),
            transports: ['websocket'],
            reconnection: true,
        });

        this.socket.on('connect', () => {
            console.log('[Socket] Connected:', this.socket?.id);
        });

        this.socket.on('connect_error', (err: any) => {
            console.error('[Socket] Connection Error:', err);
            if (SOCKET_SESSION_CODES.includes(err?.data?.code)) {
                localStorage.removeItem('jwtToken');
                window.location.href = '/login';
            }
        });

        // R3 FM5: a socket the server dropped does not reconnect by itself; the same socket keeps its listeners.
        this.socket.on('disconnect', (reason: string) => {
            if (reason === 'io server disconnect') setTimeout(() => this.socket?.connect(), RECONNECT_AFTER_SERVER_DROP_MS);
        });
    }

    joinRoom(chatRoomId: number | string) {
        if (!this.socket) this.connect();
        this.socket?.emit('join_room', { chatRoomId });
    }

    leaveRoom(chatRoomId: number | string) {
        if (!this.socket) return;
        this.socket.emit('leave_room', { chatRoomId });
    }

    onMessageReceived(callback: (message: any) => void) {
        if (!this.socket) return;
        this.socket.on('receive_message', (payload: any) => {
            console.log('[Socket] Message Received:', payload);
            callback(payload);
        });
    }

    offMessageReceived() {
        this.socket?.off('receive_message');
    }

    /** Generic emit for client-pushed events (e.g. 'chat:typing', 'chat:read'). */
    emit(event: string, payload?: any) {
        if (!this.socket) this.connect();
        this.socket?.emit(event, payload);
    }

    /** Generic subscription for server-pushed events (e.g. 'safety:sos'). */
    on(event: string, callback: (payload: any) => void) {
        if (!this.socket) this.connect();
        this.socket?.on(event, callback);
    }

    off(event: string, callback?: (payload: any) => void) {
        if (callback) this.socket?.off(event, callback);
        else this.socket?.off(event);
    }

    disconnect() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }
    }
}

export const socketService = new SocketService();